import { apiFetch, clearAuth, getCsrfToken, setAccessToken, setActiveRegionOverride, setCsrfToken, } from './client';
/**
 * GET /auth/sso/discover?email=<email> — pre-session bootstrap call. `rememberMe` ("Keep me
 * signed in") rides the signed SSO state through the IdP round trip, so the callback plants a
 * persistent sign-in cookie only when the user asked for one.
 */
export async function ssoDiscover(email, rememberMe = false) {
    const query = `email=${encodeURIComponent(email)}${rememberMe ? '&rememberMe=true' : ''}`;
    return apiFetch(`/auth/sso/discover?${query}`, {
        method: 'GET',
    });
}
/**
 * POST /auth/sso/complete — pre-session bootstrap call (authenticated by the
 * handoff token, not a Bearer session). Like mfaVerify, does NOT store tokens —
 * the caller must call establishSession().
 */
export async function ssoComplete(handoffToken) {
    return apiFetch('/auth/sso/complete', {
        method: 'POST',
        body: JSON.stringify({ handoffToken }),
    });
}
export async function login(credentials) {
    const response = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
    });
    // MFA-enrolled identity: the password was correct but no session exists yet —
    // the caller must complete the second step via mfaVerify().
    if ('mfaRequired' in response) {
        return response;
    }
    // Store token and CSRF token
    setAccessToken(response.accessToken.value, response.accessToken.expiresAt);
    setCsrfToken(response.csrfToken);
    // Shared-machine hygiene: clear any stale super-admin region override from a
    // previous session so the new user starts at their own default region. This is
    // login-only — refreshToken() must NOT clear here or a super admin's override
    // would reset every ~hour on silent token refresh.
    setActiveRegionOverride(null);
    return response;
}
// Second step of the two-step login. Deliberately does NOT store tokens —
// establishSession() (use-auth) owns that, same as the invite-accept flow.
export async function mfaVerify(request) {
    return apiFetch('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify(request),
    });
}
export async function mfaStatus() {
    return apiFetch('/auth/mfa', { method: 'GET' });
}
export async function mfaEnroll() {
    return apiFetch('/auth/mfa/enroll', { method: 'POST' });
}
export async function mfaConfirm(code) {
    return apiFetch('/auth/mfa/confirm', {
        method: 'POST',
        body: JSON.stringify({ code }),
    });
}
export async function mfaDisable(currentPassword) {
    await apiFetch('/auth/mfa', {
        method: 'DELETE',
        body: JSON.stringify({ currentPassword }),
    });
}
export async function mfaRegenerateRecoveryCodes(currentPassword) {
    return apiFetch('/auth/mfa/recovery-codes', {
        method: 'POST',
        body: JSON.stringify({ currentPassword }),
    });
}
// Concurrent explicit refreshes (the session bootstrap, a page forcing a fresh claim) share one
// request, as concurrent apiFetch callers already do via ensureFreshAccessToken. The server never
// rotates the CSRF token on refresh, so parallel refreshes from several tabs are each valid on
// their own: sharing one here only removes redundant calls; the slot clears when it settles so a
// failure never poisons the next try.
let inFlightRefresh = null;
export function refreshToken() {
    inFlightRefresh ??= requestRefresh().finally(() => {
        inFlightRefresh = null;
    });
    return inFlightRefresh;
}
async function requestRefresh() {
    const csrfToken = getCsrfToken();
    const headers = {};
    if (csrfToken)
        headers['X-CSRF-Token'] = csrfToken;
    const response = await apiFetch('/auth/refresh', {
        method: 'POST',
        headers,
    });
    // Update token
    setAccessToken(response.accessToken.value, response.accessToken.expiresAt);
    return response;
}
export async function logout() {
    try {
        const csrfToken = getCsrfToken();
        const headers = {};
        if (csrfToken)
            headers['X-CSRF-Token'] = csrfToken;
        await apiFetch('/auth/logout', { method: 'POST', headers });
    }
    finally {
        clearAuth();
    }
}
